/**
 * Did this sign-in come from one of the shareable links?
 *
 * WHY THIS EXISTS
 * ---------------
 * An account linked to no institute is stopped at sign-in with "No access yet"
 * (see `ChooseContext/useCompleteSignIn`). That is right for someone who opens
 * the app directly: there is nothing in it for them yet. It is wrong for the
 * people these four links are sent to, who by definition are NOT in an
 * institute yet — the link is how they get in touch with one:
 *
 *     registration form    /forms/users/<token>
 *     public test          /test/instructions/<id>
 *     batch join           /batch-request/<token>
 *     employee onboarding  /employeeOnbard  (and /employee-onboarding)
 *
 * Those pages send a signed-out visitor to /login carrying their own address,
 * and the sign-in must be allowed to finish so the visitor can come back to it.
 *
 * THE DESTINATION DECIDES, NOT WHETHER THERE IS ONE
 * -------------------------------------------------
 * Every sign-in carries a return target, including a direct one: it defaults to
 * the dashboard, and the Login <-> Sign up links and the 2FA hop pass it along
 * explicitly. So "has a target" cannot mean "came from a link" — the target has
 * to be one of these four pages. Other screens use `?redirect=` too (the SCORM
 * builder, CLI auth), and they are not a way in for someone with no institute.
 *
 * REMEMBERED FOR A SHORT WHILE
 * ----------------------------
 * The target lives in the /login URL or router state, and two common paths lose
 * it: the verification email after an email sign-up opens a NEW tab, and the
 * 2FA page's error paths go back to a bare /login. So a link target is also kept
 * in localStorage for 30 minutes and read when a sign-in page has no explicit
 * target of its own. Finishing or refusing a sign-in clears it.
 */

import type { ContextOutcome } from "./ChooseContext/useLoginContexts";

/** The four link pages, by the path each one lands on. Case-insensitive, like the router. */
const LINK_PAGES: ReadonlyArray<RegExp> = [
  /^\/forms\/users\/[^/]+\/?$/i, // registration form
  /^\/test\/instructions\/[^/]+\/?$/i, // public test
  /^\/batch-request\/[^/]+\/?$/i, // batch join
  /^\/(employeeOnbard|employee-onboarding)\/?$/i, // employee onboarding
];

const PENDING_KEY = "pending_link_return";

/** How long a remembered link target stays usable. */
export const PENDING_LINK_TTL_MS = 30 * 60 * 1000;

/** The path part of a same-origin target: no query, no hash. Null for anything else. */
const pathOf = (target: string | null | undefined): string | null => {
  if (!target || typeof target !== "string") return null;
  let value = target.trim();

  if (/^https?:\/\//i.test(value)) {
    try {
      const url = new URL(value);
      // Fail closed: an absolute URL counts only when it provably points at
      // this origin. With no window to compare against, it does not.
      if (typeof window === "undefined" || url.origin !== window.location.origin) {
        return null;
      }
      value = url.pathname;
    } catch {
      return null;
    }
  }

  if (!value.startsWith("/") || value.startsWith("//")) return null;
  return value.split(/[?#]/, 1)[0];
};

/** True when a sign-in headed here came from one of the four links. */
export const isLinkReturnTarget = (target: string | null | undefined): boolean => {
  const path = pathOf(target);
  return path !== null && LINK_PAGES.some((page) => page.test(path));
};

/** Keep a link target for the tab that may finish the sign-in. Ignores anything else. */
export const rememberLinkReturn = (
  target: string | null | undefined,
  now: number = Date.now(),
): void => {
  if (!target || !isLinkReturnTarget(target)) return;
  try {
    localStorage.setItem(PENDING_KEY, JSON.stringify({ target, at: now }));
  } catch {
    // Storage blocked — the target still travels in the URL on the normal path.
  }
};

/** The remembered link target, or null when there is none or it has lapsed. */
export const readLinkReturn = (now: number = Date.now()): string | null => {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(PENDING_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  try {
    const { target, at } = JSON.parse(raw) ?? {};
    const fresh =
      typeof at === "number" && now - at >= 0 && now - at <= PENDING_LINK_TTL_MS;
    if (fresh && isLinkReturnTarget(target)) return target;
  } catch {
    // Malformed — fall through and drop it.
  }
  clearLinkReturn();
  return null;
};

export const clearLinkReturn = (): void => {
  try {
    localStorage.removeItem(PENDING_KEY);
  } catch {
    /* storage unavailable - nothing to clear */
  }
};

/**
 * The older hand-off some pages still write before sending someone to /login.
 * Cleared whenever a sign-in finishes or is refused, so a key left behind by an
 * abandoned visit cannot make a later, direct sign-in look like a link one.
 */
export const clearLegacyReturnKeys = (): void => {
  try {
    localStorage.removeItem("isRoutingManual");
    localStorage.removeItem("returnToInvitationUrl");
    localStorage.removeItem("returnToAssessmentUrl");
  } catch {
    /* storage unavailable - nothing to clear */
  }
};

/**
 * What to do once the server has answered "what may this account sign in as".
 *
 *   finish  open the session and go to the target
 *   deny    show "No access yet" and hand the session back
 *   select  exactly one context: select it, then finish
 *   choose  several: show the picker
 *
 * Pure, so the rule every sign-in page follows is tested in one place.
 */
export type SignInStep = "finish" | "deny" | "select" | "choose";

export const nextSignInStep = (
  outcome: ContextOutcome,
  returnTarget: string | null | undefined,
): SignInStep => {
  switch (outcome) {
    case "none":
      // Linked to nothing. Only a visitor on their way back to one of the four
      // links gets through; everyone else is told why there is nothing here.
      return isLinkReturnTarget(returnTarget) ? "finish" : "deny";
    case "single":
      return "select";
    case "choose":
      return "choose";
    default:
      // 'unavailable' — feature off server-side, or unreachable. Never block a
      // sign-in on this question.
      return "finish";
  }
};
